/**
 * Signals and the Signal Feed.
 *
 * A **Signal** is one saved definition: terms, scope, body-type filter, optional category,
 * delivery frequency and channel. It drives three outputs — it fills this feed, it rolls
 * matched items sharing an ordinance number into Legislative History, and it optionally sends
 * alerts. Delivery is a *setting on* the Signal, never a separate object.
 *
 * A Signal is NOT a Topic. A Topic (Topic Trends only) counts meetings over time for a chart;
 * a Signal finds documents for a feed. They are separate collections and must not be merged,
 * however similar their creation sheets look.
 *
 * Never call these "saved searches" or "alerts" in customer-facing copy.
 */
import type { BodyType } from '@/lib/types';

/** Event type of a matched item. Extensible — the set is not closed. */
export type EventType = 'rezone' | 'morat' | 'permit' | 'utility';

export interface EventTypeMeta {
  label: string;
}

export const EVENT_TYPES: Record<EventType, EventTypeMeta> = {
  rezone: { label: 'Rezoning' },
  morat: { label: 'Moratorium' },
  permit: { label: 'Permit' },
  utility: { label: 'Utility deal' },
};

export type SignalScope = 'corpus' | 'states' | 'mytowns';
export type SignalFrequency = 'realtime' | 'daily' | 'weekly';

export interface Signal {
  id: string;
  name: string;
  /** Human-readable scope summary, e.g. "municipal · statewide". */
  meta: string;
  /** All-time match count, not just today's. */
  total: number;
  terms: string;
  scope: SignalScope;
  bodies: BodyType[];
  categories: string[];
  frequency: SignalFrequency;
  /** Delivery channels — settings on the Signal, not separate objects. */
  email: boolean;
  whatsApp: boolean;
}

/**
 * One row of the Signal Feed: a passage from a meeting that matched a Signal.
 *
 * The matched passage arrives pre-split into `before` / `hit` / `after` rather than as HTML
 * with a `<mark>` already in it. The server knows where the match is; sending markup would
 * mean trusting and injecting it, and there is no reason to.
 */
export interface SignalMatch {
  id: string;
  city: string;
  region: string;
  /** Rounded for display, e.g. "40k" — population gives an analyst a sense of scale. */
  population: string;
  body: BodyType;
  /** mm/dd/yyyy, formatted server-side. */
  date: string;
  passage: { before: string; hit: string; after: string };
  /** Extracted entities: organizations, quantities, dollar figures. */
  entities: string[];
  eventType: EventType;
  /** Vote outcome as recorded, e.g. "7–0 pass", "tabled". Never inferred. */
  vote: string | null;
  /** The spoken ordinance or resolution number, or null. */
  num: string | null;
  /** Set when this number is already part of a Legislative History record. */
  recordId: string | null;
  /** Which Signal caught this — the provenance column, and the reason the row is here. */
  signalName: string;
}

/**
 * The category a matched item is filed under, derived from its event type.
 *
 * Categories come from the summarizer's pinned taxonomy; event types are the Signal Feed's
 * own coarser grouping. This mapping is the join between them, and it lives here so the feed
 * and Search cannot drift into filing the same item differently.
 */
const EVENT_CATEGORY: Record<EventType, string> = {
  rezone: 'Urban Planning',
  morat: 'Environment',
  permit: 'Urban Planning',
  utility: 'Infrastructure',
};

export function categoryFor(eventType: EventType): string {
  return EVENT_CATEGORY[eventType];
}
